import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  /** @type {AbortController | undefined} */
  #kachingAbortController;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerenter', this.#preloadImage);
    this.#setupKachingListener();
    this.#setupVariantUpdateRestore();
    this.#setupPriceEnforcement();
    // Set initial bundle title and success message
    setTimeout(() => {
      this.#updateBundleTitle();
      this.#updateBundleSuccessMessage();
    }, 200);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    if (this.#kachingPriceDebounceId) clearTimeout(this.#kachingPriceDebounceId);
    if (this.#variantUpdateRestoreTimeout) clearTimeout(this.#variantUpdateRestoreTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
    
    if (this.#variantUpdateSection) {
      this.#variantUpdateSection.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdateRestorePrices);
      this.#variantUpdateSection = null;
    }
    this.#priceObserver?.disconnect();
    this.#priceObserver = null;
    this.#kachingPriceObserver?.disconnect();
    this.#kachingPriceObserver = null;
    if (this.#kachingAbortController) {
      this.#kachingAbortController.abort();
    }
  }

  /** @type {MutationObserver | null} */
  #priceObserver = null;

  /** @type {MutationObserver | null} */
  #kachingPriceObserver = null;

  /**
   * Watches price elements. When another script overwrites our button prices,
   * re-read from Kaching DOM and re-apply (keeps prices in sync with Kaching's currency-aware values).
   */
  #setupPriceEnforcement() {
    const button = this.refs.addToCartButton;
    if (!button) return;
    const pricesWrapper = button.querySelector('.add-to-cart-text__prices');
    if (!pricesWrapper) return;

    this.#priceObserver = new MutationObserver(() => {
      if (this.#isUpdatingPrices) return;
      const { price, strikethrough } = this.#getBundlePriceForButton();
      if (price) {
        const strikethroughEl = button.querySelector('.add-to-cart-text__strikethrough-price');
        const currentPriceEl = button.querySelector('.add-to-cart-text__current-price');
        const currentStrike = strikethroughEl?.textContent?.trim() || '';
        const currentPrice = currentPriceEl?.textContent?.trim() || '';
        const expectedStrike = strikethrough || '';
        const expectedPrice = price;
        if (currentStrike !== expectedStrike || currentPrice !== expectedPrice) {
          this.#updateAddToCartButtonPrices(price, strikethrough);
        }
      }
    });
    this.#priceObserver.observe(pricesWrapper, { childList: true, subtree: true, characterData: true });
  }

  /** @type {boolean} */
  #isUpdatingPrices = false;

  /**
   * Sets up listener for Kaching bundle selection events to update button text with price.
   */
  #setupKachingListener() {
    // Store original text immediately
    const buttonTextElement = this.refs.addToCartButton?.querySelector('.add-to-cart-text__content');
    if (buttonTextElement) {
      this.#originalButtonText = buttonTextElement.textContent.trim();
    }

    // Try multiple selectors for Kaching block
    const kachingSelectors = [
      'kaching-bundles-block',
      '[data-kaching-bundles]',
      'kaching-bundle-block',
      '[id*="kaching"]',
      '[class*="kaching"]'
    ];

    const findKachingBlock = () => {
      for (const selector of kachingSelectors) {
        const block = document.querySelector(selector);
        if (block) {
          return block;
        }
      }
      return null;
    };

    // Wait for Kaching bundles block to be available
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max wait
    let kachingSetupDone = false;

    const checkForKaching = () => {
      if (kachingSetupDone) return;
      const kachingBlock = findKachingBlock();
      if (kachingBlock) {
        kachingSetupDone = true;
        this.#kachingAbortController = new AbortController();

        // Listen on both the block and document (event might bubble)
        const handler = (event) => {
          this.#handleKachingPriceUpdate(event);
        };

        kachingBlock.addEventListener('variants-changed', handler, {
          signal: this.#kachingAbortController.signal
        });

        document.addEventListener('variants-changed', handler, {
          signal: this.#kachingAbortController.signal
        });

        document.addEventListener('click', (event) => {
          if (event.target.closest('.kaching-bundles__bar')) {
            setTimeout(() => {
              this.#updateBundleTitle();
              this.#updateBundleSuccessMessage();
              const { price, strikethrough } = this.#getBundlePriceForButton();
              if (price) this.#updateAddToCartButtonPrices(price, strikethrough);
            }, 50);

            setTimeout(() => {
              this.#updateBundleTitle();
              this.#updateBundleSuccessMessage();
            }, 200);
          }
        }, {
          signal: this.#kachingAbortController.signal,
          capture: true
        });

        // Observe Kaching price elements so we sync when they change (e.g. currency/country switch)
        this.#kachingPriceObserver = new MutationObserver(() => {
          this.#handleKachingPriceUpdate();
        });
        this.#kachingPriceObserver.observe(kachingBlock, {
          childList: true,
          subtree: true,
          characterData: true,
          characterDataOldValue: false,
          attributes: true,
          attributeFilter: ['class', 'aria-selected', 'aria-checked']
        });

        // Initial sync: wait for Kaching to settle before reading
        setTimeout(() => {
          const { price, strikethrough } = this.#getBundlePriceForButton();
          if (price) this.#updateAddToCartButtonPrices(price, strikethrough);
          this.#updateBundleSuccessMessage();
          this.#updateBundleTitle();
        }, 600);
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(checkForKaching, 100);
      }
    };

    checkForKaching();
    setTimeout(checkForKaching, 400);
  }

  /** @type {Element | null} */
  #variantUpdateSection = null;
  /** @type {number | undefined} */
  #variantUpdateRestoreTimeout;

  /**
   * Listens for variant:update. The theme morphs the add-to-cart button with server HTML
   * that has wrong compare-at price for bundles. We re-apply our static bundle prices after.
   */
  #setupVariantUpdateRestore() {
    this.#variantUpdateSection = this.closest('.shopify-section');
    if (this.#variantUpdateSection) {
      this.#variantUpdateSection.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdateRestorePrices);
    }
  }

  #onVariantUpdateRestorePrices = () => {
    if (this.#variantUpdateRestoreTimeout) clearTimeout(this.#variantUpdateRestoreTimeout);
    this.#variantUpdateRestoreTimeout = setTimeout(() => {
      this.#variantUpdateRestoreTimeout = undefined;
      const { price, strikethrough } = this.#getBundlePriceForButton();
      if (price) this.#updateAddToCartButtonPrices(price, strikethrough);
    }, 150);
  };

  /** @type {number | undefined} */
  #kachingPriceDebounceId;

  /**
   * Handles Kaching bundle price updates and updates the Add to Cart button.
   * Debounced to avoid rapid overwrites when Kaching/selection settles.
   */
  #handleKachingPriceUpdate = (event) => {
    if (this.#kachingPriceDebounceId) clearTimeout(this.#kachingPriceDebounceId);
    this.#kachingPriceDebounceId = setTimeout(() => {
      this.#kachingPriceDebounceId = undefined;
      const { price, strikethrough } = this.#getBundlePriceForButton();
      this.#updateAddToCartButtonPrices(price, strikethrough);
      this.#updateBundleTitle();
      this.#updateBundleSuccessMessage();
    }, 50);
  };

  /**
   * Gets price and strikethrough for the add to cart button from Kaching DOM.
   * Reads from the selected bundle bar so prices stay in sync with currency (kr, €, etc.).
   */
  #getBundlePriceForButton() {
    const kachingBlock = document.querySelector('kaching-bundles-block') || document.querySelector('[data-kaching-bundles]');
    if (!kachingBlock) return { price: null, strikethrough: null };

    const selectedBar = kachingBlock.querySelector('.kaching-bundles__bar--selected');
    if (!selectedBar) return { price: null, strikethrough: null };

    const priceEl = selectedBar.querySelector('.kaching-bundles__bar-price');
    const fullPriceEl = selectedBar.querySelector('.kaching-bundles__bar-full-price');
    const price = priceEl?.textContent?.trim() || null;
    const strikethrough = fullPriceEl?.textContent?.trim() || null;
    return { price, strikethrough };
  }

  /**
   * Updates the Add to Cart button with price display.
   * Strikethrough is a hardcoded number (e.g. 120€), not from compare_at_price.
   */
  #updateAddToCartButtonPrices(price, strikethrough) {
    const button = this.refs.addToCartButton;
    if (!button) return;

    const textContent = button.querySelector('.add-to-cart-text__content');
    const pricesWrapper = button.querySelector('.add-to-cart-text__prices');
    const strikethroughEl = button.querySelector('.add-to-cart-text__strikethrough-price');
    const currentPriceEl = button.querySelector('.add-to-cart-text__current-price');

    if (!textContent || !pricesWrapper || !strikethroughEl || !currentPriceEl) return;

    this.#isUpdatingPrices = true;
    const rawText = textContent.textContent || '';
    const baseText = this.#originalButtonText || rawText.replace(/\s+[€$£¥]?[\d\s.,]+\s*$/, '').trim() || 'Add to cart';
    if (!this.#originalButtonText) this.#originalButtonText = baseText;

    if (price) {
      textContent.textContent = baseText;
      currentPriceEl.textContent = price;

      if (strikethrough && strikethrough.trim()) {
        strikethroughEl.textContent = strikethrough;
        strikethroughEl.style.display = 'inline';
      } else {
        strikethroughEl.textContent = '';
        strikethroughEl.style.display = 'none';
      }

      pricesWrapper.style.display = 'inline-flex';
    } else {
      textContent.textContent = baseText;
      strikethroughEl.textContent = '';
      currentPriceEl.textContent = '';
      pricesWrapper.style.display = 'none';
    }
    this.#isUpdatingPrices = false;
  }

  /**
   * Gets the selected bundle ID from the Kaching DOM (L_Qx=B2G1Free, X3qO=B3G2Free, vJAA=1Card).
   * @returns {string | null}
   */
  #getSelectedBundleId() {
    const kachingBlock = document.querySelector('kaching-bundles-block') || document.querySelector('[data-kaching-bundles]');
    if (!kachingBlock) return null;
    const selectedBar = kachingBlock.querySelector('.kaching-bundles__bar--selected');
    if (!selectedBar) return null;

    let bundleId = selectedBar.getAttribute('data-deal-bar-id') || selectedBar.getAttribute('data-bundle-id');
    if (bundleId) return bundleId;

    const titleEl = selectedBar.querySelector('.kaching-bundles__bar-title');
    const title = (titleEl?.textContent || '').toLowerCase();
    if (title.includes('buy 2') || title.includes('2 get 1')) return 'L_Qx';
    if (title.includes('buy 3') || title.includes('3 get 2')) return 'X3qO';
    if (title.includes('1 card') && !title.includes('get')) return 'vJAA';
    return null;
  }

  /**
   * Updates the bundle success message above the buy button based on selected bundle.
   * B2G1Free: "Congrats! You've got 1 card free."
   * B3G2Free: "Congrats! You've got 2 free cards."
   * 1 Card: hide message.
   */
  #updateBundleSuccessMessage() {
    const form = this.closest('form');
    const messageEl = form?.querySelector('.bundle-success-message');
    const textEl = messageEl?.querySelector('.bundle-success-message__text');
    if (!messageEl || !textEl) return;

    const bundleId = this.#getSelectedBundleId();
    if (bundleId === 'L_Qx') {
      textEl.textContent = "Congrats! You've got 1 tracking card free.";
      messageEl.classList.remove('hidden');
    } else if (bundleId === 'X3qO') {
      textEl.textContent = "Congrats! You've got 2 free tracking cards + free Wireless Charger.";
      messageEl.classList.remove('hidden');
    } else {
      messageEl.classList.add('hidden');
      textEl.textContent = '';
    }
  }

  /**
   * Updates bundle title with card count from selected bundle.
   */
  #updateBundleTitle = () => {
    const titleEl = document.querySelector('.kaching-bundles__block-title');
    const kachingBlock = document.querySelector('kaching-bundles-block') || document.querySelector('[data-kaching-bundles]');

    if (!titleEl || !kachingBlock) return;

    const selectedBar = kachingBlock.querySelector('.kaching-bundles__bar--selected');
    const barTitle = selectedBar?.querySelector('.kaching-bundles__bar-title');

    if (!selectedBar || !barTitle) return;

    const titleHtml = (barTitle.innerHTML || '').replace(/\s+/g, ' ').trim();
    const text = (barTitle.innerText || barTitle.textContent || '').replace(/\s+/g, ' ').trim();

    let count = '1 Card';

    // Exact Kaching app HTML title formats:
    // <span style="font-weight: 400;">Buy 2</span><br> <strong>Get 1 FREE</strong>
    // <span style="font-weight: 400;">Buy 3</span><br> <strong>Get 2 FREE</strong>
    if (
      titleHtml.includes('Buy 2</span><br> <strong>Get 1 FREE</strong>') ||
      text.match(/Buy\s*2\s*Get\s*1\s*FREE/i)
    ) {
      count = '3 Cards';
    } else if (
      titleHtml.includes('Buy 3</span><br> <strong>Get 2 FREE</strong>') ||
      text.match(/Buy\s*3\s*Get\s*2\s*FREE/i)
    ) {
      count = '5 Cards + Free Charger';
    } else {
      // Supports both:
      // "Buy 2 Get 1 FREE"
      // "Buy 2 Get + 1 FREE"
      const buyMatch = text.match(/Buy\s+(\d+)/i);
      const getMatch = text.match(/Get\s*(?:\+)?\s*(\d+)/i);

      if (buyMatch && getMatch) {
        const total = parseInt(buyMatch[1], 10) + parseInt(getMatch[1], 10);
        count = total === 1 ? '1 Card' : `${total} Cards`;
      } else {
        const cardMatch = text.match(/(\d+)\s*Card/i);

        if (cardMatch) {
          const num = parseInt(cardMatch[1], 10);
          count = num === 1 ? '1 Card' : `${num} Cards`;
        }
      }
    }

    titleEl.innerHTML = `Bundle & Save: <span class="bundle-card-count">${count}</span>`;
  };

  /** @type {string | undefined} */
  #originalButtonText;

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const quantitySelector = /** @type {any} */ (form.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const cartItem = cart.items.find(
        /** @param {any} item */
        (item) => item.variant_id.toString() === variantIdInput.value.toString()
      );
      const cartQty = cartItem ? cartItem.quantity : 0;

      // Use public API to update quantity selector
      const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
      if (quantitySelector?.setCartQuantity) {
        quantitySelector.setCartQuantity(cartQty);
      }

      // Update quantity label if it exists
      this.#updateQuantityLabel(cartQty);

      return cartQty;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError, addToCartButtonContainer } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (addToCartButtonContainer?.refs.addToCartButton?.disabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        addToCartButtonContainer?.disable();

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity.toString());
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          addToCartButtonContainer?.enable();
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';

    if (!currentAddToCartButton && !this.refs.acceleratedCheckoutButtonContainer) return;

    // Update the button state
    if (currentAddToCartButton) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        addToCartButtonContainer.disable();
      } else {
        addToCartButtonContainer.enable();
      }

      // Update the add to cart button text and icon
      if (newAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
      }
    }

    if (this.refs.acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(
        newQuantityInput.min,
        newQuantityInput.max || null,
        newQuantityInput.step
      );
    }

    // Check if quantity rules are appearing/disappearing (causes layout shift)
    const quantityRules = this.refs.quantityRules;
    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    if (isQuantityRulesChanging && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const currentProductFormButtons = this.refs.productFormButtons;
      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (currentProductFormButtons && newProductFormButtons) {
        morph(currentProductFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (newQuantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          newQuantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          newQuantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      const quantityLabel = this.querySelector('.quantity-label');
      const newQuantityLabel = event.detail.data.html.querySelector('.quantity-label');
      this.#morphOrUpdateElement(quantityLabel, newQuantityLabel, quantitySelector);

      const addToCartButton = this.querySelector('[ref="addToCartButtonContainer"]');
      this.#morphOrUpdateElement(quantityRules, newQuantityRules, addToCartButton);
    }

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}